import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import useItemStore from '@store/useItemStore'
import itemService from '@services/itemService'
import { Modal, Button, ConfirmDialog } from '@components/common'
import { Download, RotateCcw, User, Calendar } from 'lucide-react'

/**
 * ItemVersions Component
 *
 * Modal for viewing version history and managing versions.
 */
const ItemVersions = ({ onClose }) => {
  const {
    selectedItem,
    versions,
    versionsLoading,
    fetchVersions,
    rollbackVersion,
    downloadItem,
  } = useItemStore()

  const [showRollbackConfirm, setShowRollbackConfirm] = useState(false)
  const [selectedVersion, setSelectedVersion] = useState(null)

  useEffect(() => {
    if (selectedItem?.id) {
      fetchVersions(selectedItem.id)
    }
  }, [selectedItem?.id])

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const handleDownload = (version) => {
    downloadItem(selectedItem.id, version.is_current ? null : version.version)
  }

  const handleRollbackClick = (version) => {
    setSelectedVersion(version)
    setShowRollbackConfirm(true)
  }

  const handleRollbackConfirm = async () => {
    if (!selectedVersion) return
    await rollbackVersion(selectedItem.id, selectedVersion.version)
    setShowRollbackConfirm(false)
    setSelectedVersion(null)
  }

  if (!selectedItem) return null

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Version History: ${selectedItem.title}`}
      size="medium"
      closeOnBackdrop={false}
    >
      <div className="item-versions">
        {versionsLoading ? (
          <div className="loading-state">Loading version history...</div>
        ) : versions.length === 0 ? (
          <div className="empty-state">No version history available.</div>
        ) : (
          <ul className="version-timeline">
            {versions.map((version, index) => (
              <li
                key={version.id || `current-${index}`}
                className={`version-timeline__item ${version.is_current ? 'version-timeline__item--current' : ''}`}
              >
                <div className="version-timeline__marker" />
                <div className="version-timeline__content">
                  <div className="version-timeline__header">
                    <span className="version-timeline__version">
                      Version {version.version}
                    </span>
                    {version.is_current && (
                      <span className="version-timeline__current-badge">Current</span>
                    )}
                  </div>
                  <div className="version-timeline__date">
                    <Calendar size={12} />
                    {formatDate(version.created_at)}
                  </div>
                  {version.change_reason && (
                    <p className="version-timeline__reason">{version.change_reason}</p>
                  )}
                  <div className="version-timeline__file">
                    {version.original_name} ({itemService.formatFileSize(version.file_size)})
                  </div>
                  {version.creator && (
                    <div className="version-timeline__creator">
                      <User size={12} />
                      {version.creator.name || version.creator.email}
                    </div>
                  )}
                  <div className="version-timeline__actions">
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => handleDownload(version)}
                    >
                      <Download size={14} />
                      Download
                    </Button>
                    {!version.is_current && (
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => handleRollbackClick(version)}
                      >
                        <RotateCcw size={14} />
                        Rollback
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      {/* Rollback Confirmation */}
      {showRollbackConfirm && selectedVersion && (
        <ConfirmDialog
          isOpen={true}
          onConfirm={handleRollbackConfirm}
          onCancel={() => {
            setShowRollbackConfirm(false)
            setSelectedVersion(null)
          }}
          title="Rollback to Version?"
          message={`Are you sure you want to rollback to version ${selectedVersion.version}? This will create a new version with the content from version ${selectedVersion.version}.`}
          confirmText="Rollback"
          variant="warning"
        />
      )}
    </Modal>
  )
}

ItemVersions.propTypes = {
  onClose: PropTypes.func.isRequired,
}

export default ItemVersions
